/**
 * ALTIORA · DashboardPreview — Vitrine visuelle du produit.
 *
 * Objectif : montrer, pas dire. Le visiteur voit un tableau de bord
 * ALTIORA reconstitué (mock institutionnel) juste après le hero, pour
 * comprendre en moins de 30 secondes ce que fait la plateforme.
 *
 * 4 modules visibles simultanément :
 *   - Morning Command Center (priorités du jour)
 *   - Trajectory Score (jauge)
 *   - Opportunités détectées (feed)
 *   - Analyse de risque (mini-tile)
 *
 * Tous les chiffres sont des mockups fixes — pas d'API. C'est une
 * démonstration visuelle, pas un extract live.
 */
import { motion } from "framer-motion";
import {
  Sun, TrendingUp, AlertTriangle, Sparkles, ChevronRight,
  Target, Compass, LineChart as LineIcon, Clock,
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

const NAVY = "#0B2545";
const NAVY_DEEP = "#061528";
const CHAMPAGNE = "#C5A880";
const GOLD = "#B98F3D";
const IVORY = "#FBF6EA";
const CANVAS = "#FCFBF7";
const HAIRLINE = "#E8E1D0";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const SLATE = "#64748B";
const GREEN = "#0B7A3B";
const AMBER = "#B45309";
const RED = "#B91C1C";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 0.68, 0.24, 1] },
};

// ── Sous-composant : jauge de score (SVG radial) ───────────────────
function ScoreGauge({ value = 78, size = 148, stroke = 10 }) {
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = Math.PI * r; // demi-cercle
  const progress = (value / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size / 2 + 24 }}>
      <svg width={size} height={size / 2 + 4} viewBox={`0 0 ${size} ${size / 2 + 4}`}
           style={{ position: "absolute", top: 0, left: 0 }}>
        <path
          d={`M ${stroke / 2} ${cy} A ${r} ${r} 0 0 1 ${size - stroke / 2} ${cy}`}
          stroke={`${IVORY}22`} strokeWidth={stroke} fill="none" strokeLinecap="round"
        />
        <motion.path
          d={`M ${stroke / 2} ${cy} A ${r} ${r} 0 0 1 ${size - stroke / 2} ${cy}`}
          stroke={GOLD} strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          whileInView={{ strokeDashoffset: circumference - progress }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: [0.16, 0.68, 0.24, 1] }}
        />
      </svg>
      <div className="absolute" style={{ top: size / 2 - 32, left: 0, right: 0, textAlign: "center" }}>
        <div className="font-serif text-4xl tracking-tight leading-none" style={{ color: IVORY }}>{value}</div>
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] mt-1" style={{ color: `${IVORY}88` }}>
          / 100
        </div>
      </div>
    </div>
  );
}

// ── Sous-composant : barre horizontale ─────────────────────────────
function BarLine({ label, value, color = NAVY }) {
  return (
    <div className="text-[11px]">
      <div className="flex items-center justify-between mb-1">
        <span style={{ color: SLATE }}>{label}</span>
        <span className="font-mono" style={{ color: INK }}>{value}</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: `${CHAMPAGNE}22` }}>
        <motion.div
          className="h-full rounded-full"
          style={{ background: color }}
          initial={{ width: 0 }}
          whileInView={{ width: `${value}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

// ── Sous-composant : sparkline (SVG mini courbe) ──────────────────
function Sparkline({ points, color = GOLD, width = 120, height = 32 }) {
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const path = points.map((p, i) => {
    const x = i * step;
    const y = height - ((p - min) / range) * height;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <motion.path
        d={path}
        stroke={color}
        strokeWidth={1.8}
        fill="none"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: [0.16, 0.68, 0.24, 1] }}
      />
    </svg>
  );
}

// ── Sous-composant : pastille signal ───────────────────────────────
function SignalDot({ color = GREEN }) {
  return (
    <span className="relative inline-flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
            style={{ background: color }} />
      <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: color }} />
    </span>
  );
}


// ── Sous-composant : tooltip stylisé « ALTIORA propose » ───────────
function ActionTooltip({ children, action, testid }) {
  return (
    <Tooltip delayDuration={120}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        sideOffset={10}
        className="max-w-xs !bg-transparent !p-0 !text-[13px]"
        data-testid={testid}
      >
        <div
          className="rounded-lg p-4 shadow-xl"
          style={{
            background: IVORY,
            color: INK,
            border: `1px solid ${GOLD}55`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={11} strokeWidth={2} style={{ color: GOLD }} />
            <span className="text-[10px] font-mono uppercase tracking-[0.24em]" style={{ color: GOLD }}>
              ALTIORA propose
            </span>
          </div>
          <div className="text-[12.5px] leading-[1.55]" style={{ color: INK_SOFT }}>
            {action}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}


export default function DashboardPreview() {
  const priorities = [
    {
      icon: Target,
      label: "Relancer Cabinet Dubois — mandat 220 k",
      hint: "Fenêtre optimale · 2h",
      impact: "+ 3.4 pts",
      color: GOLD,
      criticality: "critical_now",
      criticalityLabel: "Critique maintenant",
      timeToCritical: "2 heures",
      action:
        "ALTIORA a détecté un pic d'engagement (2 emails + 1 visite site en 48 h). " +
        "Un appel dans les 2 prochaines heures maximise la probabilité de conversion à 68 %.",
    },
    {
      icon: Compass,
      label: "Programmer diagnostic Groupe Létourneau",
      hint: "Signal détecté ce matin",
      impact: "+ 2.1 pts",
      color: CHAMPAGNE,
      criticality: "important_today",
      criticalityLabel: "Important aujourd'hui",
      timeToCritical: "aujourd'hui",
      action:
        "Nouveau signal LinkedIn : Groupe Létourneau vient d'ouvrir un poste de CFO. " +
        "ALTIORA suggère un diagnostic stratégique d'ici 7 jours pour se positionner.",
    },
    {
      icon: Sparkles,
      label: "Envoyer proposition · Dossier Bergeron",
      hint: "Deadline dans 36 h",
      impact: "+ 1.8 pts",
      color: NAVY,
      criticality: "to_plan",
      criticalityLabel: "À planifier",
      timeToCritical: "36 heures",
      action:
        "La proposition Bergeron est prête à 92 % dans votre workspace. " +
        "ALTIORA a pré-rempli les sections chiffrées ; il ne reste qu'à valider.",
    },
  ];

  const opportunities = [
    {
      title: "Nouvelle inc. · Cabinet Nadeau Fiscalité",
      meta: "REQ · Lévis · Comptable · il y a 12 min",
      tag: "Haute",
      color: GOLD,
      criticality: "important_today",
      windowLabel: "Fenêtre 48 h",
      action:
        "Immatriculation détectée dans le REQ (3 associés). Score d'affinité 82/100, " +
        "aucun conseiller existant. Approche personnalisée sous 48 h recommandée.",
    },
    {
      title: "Événement · Réseautage CFA Québec",
      meta: "CFA · 18 févr. · Networking",
      tag: "Moyenne",
      color: CHAMPAGNE,
      criticality: "to_plan",
      windowLabel: "Cette semaine",
      action:
        "42 professionnels de votre secteur inscrits, dont 6 dans votre pipeline. " +
        "ALTIORA suggère 3 contacts prioritaires à cibler durant l'événement.",
    },
    {
      title: "Appel d'offres · Ville de Sherbrooke",
      meta: "SEAO · Consultant · Publié aujourd'hui",
      tag: "Haute",
      color: GOLD,
      criticality: "to_plan",
      windowLabel: "Avant J+21",
      action:
        "Correspondance forte avec 4 mandats similaires que vous avez remportés. " +
        "Budget estimé 180 k · date de dépôt J+21 · probabilité 47 %.",
    },
  ];

  return (
    <TooltipProvider delayDuration={120}>
    <section
      id="section-dashboard-preview"
      className="relative border-t"
      style={{ borderColor: HAIRLINE, background: CANVAS }}
      data-testid="section-dashboard-preview"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20 md:pb-32">
        {/* En-tête section */}
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.32em]"
                 style={{ color: CHAMPAGNE }}>
              <span className="h-px w-6" style={{ background: CHAMPAGNE }} />
              La plateforme en direct
            </div>
            <div className="mt-6 font-serif leading-[1.1] tracking-tight max-w-3xl"
                 style={{ color: INK, fontSize: "clamp(1.9rem, 3.8vw, 2.8rem)", fontWeight: 400 }}>
              Voyez ALTIORA
              <span style={{ fontStyle: "italic", color: GOLD }}> réfléchir</span>.
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}
                      className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest"
                      style={{ color: SLATE }}>
            <SignalDot color={GREEN} />
            <span>Données mises à jour · en continu</span>
          </motion.div>
        </div>

        {/* Le dashboard mock */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          className="mt-12 rounded-2xl overflow-hidden shadow-2xl"
          style={{ background: NAVY_DEEP, border: `1px solid ${NAVY}66` }}
          data-testid="dashboard-preview-shell"
        >
          {/* Barre de titre style macOS */}
          <div className="flex items-center justify-between px-5 py-3"
               style={{ background: NAVY, borderBottom: `1px solid ${CHAMPAGNE}22` }}>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF5F57" }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FEBC2E" }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#28C840" }} />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: `${IVORY}88` }}>
              ALTIORA · Morning Command Center
            </div>
            <div className="text-[10px] font-mono" style={{ color: `${IVORY}66` }}>
              07:42 · Mardi
            </div>
          </div>

          {/* Grille tableau de bord */}
          <div className="p-5 md:p-7 grid grid-cols-1 lg:grid-cols-3 gap-5" style={{ background: NAVY_DEEP, color: IVORY }}>

            {/* Colonne gauche : Salutation + Priorités */}
            <div className="lg:col-span-2 rounded-xl p-5"
                 style={{ background: `${IVORY}08`, border: `1px solid ${CHAMPAGNE}22` }}
                 data-testid="preview-priorities">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
                     style={{ color: CHAMPAGNE }}>
                  <Sun size={12} strokeWidth={1.6} />
                  Priorités du jour · voix du directeur
                </div>
                <SignalDot color={GOLD} />
              </div>
              <div className="mt-4 font-serif text-[22px] md:text-[26px] leading-tight tracking-tight"
                   style={{ color: IVORY }}>
                Trois actions à haute valeur aujourd'hui —
                <span style={{ fontStyle: "italic", color: GOLD }}> gains estimés + 7.3 pts</span>.
              </div>
              <div className="mt-6 space-y-3">
                {priorities.map((p, i) => (
                  <ActionTooltip
                    key={p.label}
                    action={p.action}
                    testid={`preview-priority-tooltip-${i}`}
                  >
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.25 + i * 0.12, duration: 0.6 }}
                      whileHover={{ y: -2 }}
                      className="w-full rounded-lg p-3 flex items-center gap-4 group cursor-pointer text-left transition-all"
                      style={{ background: `${IVORY}05`, border: `1px solid ${CHAMPAGNE}18` }}
                      data-testid={`preview-priority-${i}`}
                      aria-label={`${p.label} — voir la proposition ALTIORA`}
                    >
                      {/* Pastille de criticité temporelle */}
                      <span
                        className="relative inline-flex flex-shrink-0"
                        aria-label={p.criticalityLabel}
                        title={p.criticalityLabel}
                        data-testid={`preview-priority-crit-${i}`}
                      >
                        <span className="animate-ping absolute inline-flex h-2.5 w-2.5 rounded-full opacity-60"
                              style={{ background:
                                p.criticality === "critical_now" ? "#EF4444" :
                                p.criticality === "important_today" ? "#F59E0B" :
                                p.criticality === "to_plan" ? "#EAB308" : "#10B981" }} />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5"
                              style={{ background:
                                p.criticality === "critical_now" ? "#EF4444" :
                                p.criticality === "important_today" ? "#F59E0B" :
                                p.criticality === "to_plan" ? "#EAB308" : "#10B981" }} />
                      </span>
                      <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                           style={{ background: `${p.color}22`, color: p.color }}>
                        <p.icon size={16} strokeWidth={1.7} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] leading-tight truncate" style={{ color: IVORY }}>
                          {p.label}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest mt-1 flex items-center gap-1.5"
                             style={{ color: `${IVORY}66` }}>
                          <Clock size={9} strokeWidth={2} />
                          {p.criticalityLabel} · {p.timeToCritical}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-mono text-[11px] font-semibold" style={{ color: p.color }}>
                          {p.impact}
                        </div>
                      </div>
                      <ChevronRight size={13} className="flex-shrink-0 transition-transform group-hover:translate-x-0.5"
                                    style={{ color: `${IVORY}44` }} />
                    </motion.button>
                  </ActionTooltip>
                ))}
              </div>
            </div>

            {/* Colonne droite : Score de trajectoire */}
            <div className="rounded-xl p-5"
                 style={{ background: `${IVORY}08`, border: `1px solid ${CHAMPAGNE}22` }}
                 data-testid="preview-trajectory-score">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: CHAMPAGNE }}>
                Trajectoire · Score
              </div>
              <div className="mt-4 flex flex-col items-center">
                <ScoreGauge value={78} />
                <div className="mt-3 flex items-center gap-1.5 text-[11px]" style={{ color: GREEN }}>
                  <TrendingUp size={12} strokeWidth={2} />
                  <span className="font-mono">+4.2 pts · 30 j</span>
                </div>
              </div>
              <div className="mt-5 space-y-2.5">
                <BarLine label="Commercial"  value={82} color={GOLD} />
                <BarLine label="Revenus"     value={74} color={CHAMPAGNE} />
                <BarLine label="Marketing"   value={61} color={`${IVORY}66`} />
                <BarLine label="Réseau"      value={88} color={GREEN} />
              </div>
            </div>

            {/* Ligne du bas : Opportunités + Risque + Simulateur */}
            <div className="lg:col-span-2 rounded-xl p-5"
                 style={{ background: `${IVORY}08`, border: `1px solid ${CHAMPAGNE}22` }}
                 data-testid="preview-opportunities">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: CHAMPAGNE }}>
                  Opportunités détectées · 3 nouvelles
                </div>
                <SignalDot color={GOLD} />
              </div>
              <div className="mt-4 space-y-3">
                {opportunities.map((o, i) => (
                  <ActionTooltip
                    key={o.title}
                    action={o.action}
                    testid={`preview-opportunity-tooltip-${i}`}
                  >
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, y: 8 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.35 + i * 0.1, duration: 0.5 }}
                      whileHover={{ y: -1 }}
                      className="w-full flex items-center gap-3 pb-3 cursor-pointer text-left"
                      style={{ borderBottom: i < opportunities.length - 1 ? `1px solid ${CHAMPAGNE}12` : "none" }}
                      data-testid={`preview-opportunity-${i}`}
                      aria-label={`${o.title} — voir l'analyse ALTIORA`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] leading-tight truncate" style={{ color: IVORY }}>
                          {o.title}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest mt-1 flex items-center gap-1.5"
                             style={{ color: `${IVORY}55` }}>
                          <Clock size={9} strokeWidth={2} />
                          {o.windowLabel} · {o.meta}
                        </div>
                      </div>
                      <span className="px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest flex-shrink-0"
                            style={{ background: `${o.color}22`, color: o.color }}>
                        {o.tag}
                      </span>
                    </motion.button>
                  </ActionTooltip>
                ))}
              </div>
            </div>

            {/* Colonne droite bas : Simulateur + Risque */}
            <div className="space-y-5">
              <div className="rounded-xl p-5"
                   style={{ background: `${IVORY}08`, border: `1px solid ${CHAMPAGNE}22` }}
                   data-testid="preview-simulator">
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: CHAMPAGNE }}>
                  Simulateur · Comparaison
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase" style={{ color: `${IVORY}66` }}>A · Attendre</div>
                    <div className="font-serif text-lg" style={{ color: IVORY }}>+ 1.2 pt</div>
                    <Sparkline points={[10, 12, 11, 13, 14, 15, 16]} color={`${IVORY}55`} width={70} height={22} />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase" style={{ color: GOLD }}>B · Agir</div>
                    <div className="font-serif text-lg" style={{ color: GOLD }}>+ 4.8 pts</div>
                    <Sparkline points={[10, 14, 18, 22, 26, 31, 38]} color={GOLD} width={70} height={22} />
                  </div>
                </div>
              </div>

              <div className="rounded-xl p-5"
                   style={{ background: `${IVORY}08`, border: `1px solid ${CHAMPAGNE}22` }}
                   data-testid="preview-risk">
                <div className="text-[10px] font-mono uppercase tracking-widest flex items-center gap-2"
                     style={{ color: AMBER }}>
                  <AlertTriangle size={11} strokeWidth={2} /> Risque · veille passive
                </div>
                <div className="mt-2 text-[13px] leading-tight" style={{ color: IVORY }}>
                  <span style={{ color: AMBER }}>2 dossiers</span> sans contact depuis 45 jours.
                </div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: `${IVORY}66` }}>
                  Reactivation suggérée · gain estimé + 5 pts
                </div>
                <div className="mt-3 pt-3 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest"
                     style={{ color: AMBER, borderTop: `1px solid ${AMBER}22` }}
                     data-testid="preview-risk-temporal">
                  <Clock size={10} strokeWidth={2} />
                  <span>Deviendra prioritaire dans 5 jours</span>
                </div>
              </div>
            </div>

          </div>
        </motion.div>

        {/* Legend under */}
        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.25 }}
          className="mt-8 max-w-2xl text-[13px]"
          style={{ color: SLATE }}
        >
          Aperçu · reconstitué à partir de données de démonstration. En
          production, chaque tuile est alimentée par vos données réelles
          et vos décisions au fil de la journée.
        </motion.p>
      </div>
    </section>
    </TooltipProvider>
  );
}
